import React, { FC, useRef, ReactNode } from 'react';
import { VM_CHART_HEIGHT, DEFAULT_PADDING } from './helpers/config';
import { Series, TooltipFn, HorizontalIndicatorLine } from '../../interfaces/Charts';
import styled from '@emotion/styled';
import { pointer } from 'd3-selection';
import { VerticalLeftValueAxis } from './axis/VerticalAxes';
import { HorizontalLine } from './shapes/HorizontalLine';
import { useResize, useTooltip } from './hooks';
import { ChartTooltip } from './tooltip';
import { format } from 'd3-format';
import { useOverlappedBars } from './hooks/useOverlappedBars';
import { roundedRect } from './shapes/RoundedRectPath';

interface OverlappedBarsChartProps {
  matrix: Series[][];
  height: number;
  tooltipFn?: TooltipFn;
  lineTooltipfn?: (line: HorizontalIndicatorLine) => ReactNode;
  axisFormatter?: (unformatted: number) => string;
  extraHorizontalLines?: HorizontalIndicatorLine[];
  yMax?: number;
  yMin?: number;
}

const OverflowArea = styled.div`
  overflow-x: auto;
  position: relative;
  flex: 1 1 auto;
`;
const OverlappedBarsGraphContainer = styled.div`
  display: flex;
  justify-content: flex-start;
`;

const Bars = styled.g<{ dimmable: boolean }>`
  ${p =>
    p.dimmable
      ? `&:hover path {
    fill-opacity: 0.50;
  }`
      : ''}
`;
const BarGroup = styled.g`
  &:hover > path {
    fill-opacity: 1 !important;
  }
`;

const BAR_GAP = 9;

export const OverlappedBarsChart: FC<OverlappedBarsChartProps> = props => {
  const currentLineRef = useRef<number | null>(null);
  const axisFormatter = props.axisFormatter ? props.axisFormatter : format(',');
  const extraHorizontalLines = props.extraHorizontalLines ? props.extraHorizontalLines : [];
  const graphContainer = useRef<HTMLDivElement>(null);
  const height = props.height ? props.height : VM_CHART_HEIGHT;
  const graphWidth = useResize(graphContainer) - DEFAULT_PADDING.left - DEFAULT_PADDING.right;

  const { plottedPoints, largestGroupSize, valueScale, ticks } = useOverlappedBars(
    props.matrix,
    height,
    props.yMax,
    props.yMin
  );

  const width = (plottedPoints.length + 1) * BAR_GAP + DEFAULT_PADDING.left;
  const svgWidth = width < graphWidth ? graphWidth : width;

  const { showTooltip, hideTooltip, tooltip, setTooltip } = useTooltip(props.tooltipFn);
  
  const onMouseMove = (ev: React.MouseEvent<SVGGElement, MouseEvent>) => {
    if (graphContainer.current && props.tooltipFn && currentLineRef.current === null) {
      const [x, y] = pointer(ev, graphContainer.current);
      const d = graphContainer.current.getBoundingClientRect();
      const xScroll = graphContainer.current.scrollLeft;

      const nearestPlottedPoint = plottedPoints
        .flat()
        .sort((a, b) => Math.abs(x + xScroll - a.x) - Math.abs(x + xScroll - b.x))
        .filter((_, idx) => idx < largestGroupSize)
        .sort((a, b) => Math.abs(y - a.y) - Math.abs(y - b.y))[0];

      showTooltip({
        ...nearestPlottedPoint,
        y: d.y + nearestPlottedPoint.y,
        x: nearestPlottedPoint.x + d.x - xScroll,
      });
    }
  };
  const onMouseOverLine = (
    ev: React.MouseEvent<SVGPathElement, MouseEvent>,
    line: HorizontalIndicatorLine,
    index: number
  ) => {
    if (props.lineTooltipfn) {
      ev.stopPropagation();
      if (!graphContainer.current) {
        return;
      }
      currentLineRef.current = index;
      const d = graphContainer.current.getBoundingClientRect();
      hideTooltip();
      setTooltip({
        x: ev.clientX,
        y: d.y + valueScale(line.value),
        message: props.lineTooltipfn(line),
        visible: true,
      });
    }
  };

  const barsZero = valueScale(0);

  return (
    <OverlappedBarsGraphContainer>
      <div>
        <VerticalLeftValueAxis
          percentage={false}
          height={height}
          formatter={axisFormatter}
          valueScale={valueScale}
          ticks={ticks}
        ></VerticalLeftValueAxis>
      </div>
      <OverflowArea ref={graphContainer}>
        <svg
          height={height}
          width={svgWidth}
          preserveAspectRatio="xMinYMax slice"
          viewBox={`0 0 ${svgWidth} ${height}`}
        >
          {ticks.map((tick, i) => (
            <HorizontalLine key={i} color="#E4E4E4" y={valueScale(tick)} width={svgWidth} />
          ))}
          <Bars dimmable={true} onMouseMove={ev => onMouseMove(ev)} onMouseLeave={hideTooltip}>
            {plottedPoints.map(group => (
              <BarGroup>
                {group
                  .filter(p => p.y !== barsZero)
                  .map(p => (
                    <path
                      key={p.series.key + p.index}
                      fill={p.series.color}
                      d={roundedRect(p, barsZero)}
                    />
                  ))}
              </BarGroup>
            ))}
          </Bars>
          {extraHorizontalLines.map((line, index) => {
            return (
              <g key={index}>
                <HorizontalLine
                  strokeWidth={2}
                  width={svgWidth}
                  text={line.text}
                  color={line.color}
                  y={valueScale(line.value)}
                />
                <HorizontalLine
                  onMouseMove={ev => onMouseOverLine(ev, line, index)}
                  onMouseOut={() => {
                    currentLineRef.current = null;
                    hideTooltip();
                  }}
                  strokeWidth={10}
                  width={svgWidth}
                  text={line.text}
                  color="transparent"
                  y={valueScale(line.value)}
                />
              </g>
            );
          })}
        </svg>
        <ChartTooltip fixed={true} {...tooltip} />
      </OverflowArea>
    </OverlappedBarsGraphContainer>
  );
};
